import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';

export interface NotesBlockProps {
  value: string | null;
  canEdit: boolean;
  onSave: (notes: string | null) => Promise<unknown>;
}

const PLACEHOLDER = `**Why it fits**
- …

## What to check in the interview
- …`;

/** Markdown notes: the fit summary and what to check in the interview (feeds the proposal e-mail). */
export function NotesBlock({ value, canEdit, onSave }: NotesBlockProps) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value ?? '');
  const [busy, setBusy] = React.useState(false);

  const start = () => {
    setDraft(value ?? '');
    setEditing(true);
  };
  const save = async () => {
    setBusy(true);
    try {
      await onSave(draft.trim() === '' ? null : draft);
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the notes');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="submission-notes">Notes (markdown)</Label>
          <Textarea
            id="submission-notes"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={10}
            autoFocus
            placeholder={PLACEHOLDER}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void save();
            }}
          />
          <p className="text-xs text-muted-foreground">
            Fit summary first, then a &quot;What to check in the interview&quot; section: the proposal e-mail
            reads both from here.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Save notes
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-3">
      {value ? (
        <div className="prose prose-sm max-w-none text-foreground dark:prose-invert prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No notes yet. Write the fit summary and what to check in the interview.
        </p>
      )}
      {canEdit ? (
        <Button variant="outline" size="sm" onClick={start}>
          <Pencil aria-hidden="true" /> {value ? 'Edit notes' : 'Add notes'}
        </Button>
      ) : null}
    </div>
  );
}
